import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  Package,
  ShoppingBag,
  AlertTriangle,
  Settings,
  Loader2,
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { cn } from '../../lib/utils';
import type { NotificationDocument } from '../../types/notifications';

/** Best-effort relative time from a Firestore Timestamp | Date | undefined. */
function timeAgo(value: unknown): string {
  const date =
    value && typeof (value as any).toDate === 'function'
      ? (value as any).toDate()
      : value instanceof Date
      ? value
      : null;
  if (!date) return '';

  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function iconForCategory(category: string) {
  switch (category) {
    case 'new_order':
      return ShoppingBag;
    case 'order_reminder':
      return AlertTriangle;
    case 'low_stock':
      return Package;
    default:
      return Bell;
  }
}

interface NotificationBellProps {
  className?: string;
}

/**
 * Bell button with unread badge and a dropdown notification feed for the
 * merchant dashboard. Backed by the shared `useNotifications` hook (in-app feed
 * from Firestore `notifications`, scoped to the signed-in user).
 */
export function NotificationBell({ className }: NotificationBellProps) {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications(30);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleOpenNotification = (n: NotificationDocument) => {
    if (!n.read) markAsRead(n.id);
    setOpen(false);
    const url = n.data?.url;
    if (url && url.startsWith('/')) {
      navigate(url);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg text-heading hover:bg-gray-100 transition-colors"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-heading">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-[11px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="text-xs font-medium text-primary hover:text-primary-hover inline-flex items-center gap-1"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Feed */}
          <div className="max-h-[380px] overflow-y-auto">
            {loading ? (
              <div className="py-10 text-center">
                <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center px-6">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Bell className="h-6 w-6 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-heading">You're all caught up</p>
                <p className="text-xs text-body mt-1">
                  New orders and alerts will show up here.
                </p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = iconForCategory(n.category);
                return (
                  <button
                    key={n.id}
                    onClick={() => handleOpenNotification(n)}
                    className={cn(
                      'w-full text-left flex gap-3 px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50',
                      !n.read && 'bg-primary/[0.03]'
                    )}
                  >
                    <div
                      className={cn(
                        'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
                        n.read ? 'bg-gray-100' : 'bg-primary/10'
                      )}
                    >
                      <Icon className={cn('h-4 w-4', n.read ? 'text-gray-400' : 'text-primary')} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-heading truncate">{n.title}</p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-body line-clamp-2 mt-0.5">{n.body}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <button
            onClick={() => {
              setOpen(false);
              navigate('/settings/notifications');
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-100 text-xs font-medium text-body hover:bg-gray-50 transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            Notification settings
          </button>
        </div>
      )}
    </div>
  );
}
